/**
 * area-groups.ts — wspólne grupowanie pozycji po Obszarze (hierarchia handlowa
 * Pakiet → Obszar → Moduł). Jedna oś dla Katalogu modułów i Cennika, żeby ten
 * sam moduł leżał pod tym samym Obszarem na obu ekranach.
 *
 * Grupy sortowane po `areaSortOrder` rosnąco (nulls na koniec), a grupa „bez
 * obszaru" (areaId === null) zawsze na samym końcu. Nazwa i kolejność obszaru
 * brane z pierwszej pozycji danego obszaru (wszystkie pozycje obszaru je dzielą).
 */

export interface AreaItem {
  areaId?: string | null;
  areaName?: string | null;
  areaSortOrder?: number | null;
}

export interface AreaGroup<T extends AreaItem> {
  areaId: string | null;
  areaName: string | null;
  areaSortOrder: number | null;
  items: T[];
}

export function groupByArea<T extends AreaItem>(items: T[]): AreaGroup<T>[] {
  const map = new Map<string | null, AreaGroup<T>>();

  for (const item of items) {
    const key = item.areaId ?? null;
    if (!map.has(key)) {
      map.set(key, {
        areaId: key,
        areaName: item.areaName ?? null,
        areaSortOrder: item.areaSortOrder ?? null,
        items: [],
      });
    }
    map.get(key)!.items.push(item);
  }

  const groups = Array.from(map.values());

  groups.sort((a, b) => {
    // Grupa „bez obszaru" zawsze na końcu.
    if (a.areaId === null && b.areaId !== null) return 1;
    if (a.areaId !== null && b.areaId === null) return -1;
    if (a.areaId === null && b.areaId === null) return 0;
    // Oba mają obszar — sortuj po kolejności (nulls last).
    const sa = a.areaSortOrder ?? Number.MAX_SAFE_INTEGER;
    const sb = b.areaSortOrder ?? Number.MAX_SAFE_INTEGER;
    return sa - sb;
  });

  return groups;
}
